import type {
  ActivityDifficulty,
  DrawingActivity,
  GuidePrimitive,
  GuidedDrawingActivity,
  NormalizedGuidePoint,
} from "./activityTypes";

export const GUIDED_ACTIVITY_COUNT = 6;
export const CHALLENGE_ACTIVITY_COUNT = 12;
export const MAX_ACTIVITIES = 24;
export const MIN_GUIDE_STEPS = 5;
export const MAX_GUIDE_STEPS = 8;
export const MAX_PRIMITIVES_PER_STEP = 12;
export const MAX_TOTAL_PRIMITIVES_PER_GUIDE = 64;
export const MAX_PREVIEW_PRIMITIVES = 12;
export const MAX_POLYLINE_POINTS = 16;
export const MAX_ACTIVITY_TEXT_LENGTH = 240;

const MAX_ABSOLUTE_ARC_ANGLE = Math.PI * 2;
const ACTIVITY_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export class ActivityValidationError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = "ActivityValidationError";
  }
}

function fail(message: string): never {
  throw new ActivityValidationError(message);
}

function finite(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) fail(`${label} must be finite.`);
  return value;
}

function normalized(value: unknown, label: string): number {
  const result = finite(value, label);
  if (result < 0 || result > 1) fail(`${label} must be between 0 and 1.`);
  return result;
}

function point(value: unknown, label: string): NormalizedGuidePoint {
  if (!value || typeof value !== "object") fail(`${label} must be a point.`);
  const candidate = value as Record<string, unknown>;
  return {
    x: normalized(candidate.x, `${label}.x`),
    y: normalized(candidate.y, `${label}.y`),
  };
}

function hasLength(start: NormalizedGuidePoint, end: NormalizedGuidePoint): boolean {
  return start.x !== end.x || start.y !== end.y;
}

export function validateGuidePrimitive(value: unknown): GuidePrimitive {
  if (!value || typeof value !== "object") fail("Guide primitive must be an object.");
  const primitive = value as Record<string, unknown>;
  switch (primitive.type) {
    case "line": {
      const start = point(primitive.start, "line.start");
      const end = point(primitive.end, "line.end");
      if (!hasLength(start, end)) fail("Guide line cannot be empty.");
      return { type: "line", start, end };
    }
    case "polyline": {
      if (!Array.isArray(primitive.points)) fail("Guide polyline points are required.");
      if (primitive.points.length < 2 || primitive.points.length > MAX_POLYLINE_POINTS) {
        fail(`Guide polyline must contain 2 to ${MAX_POLYLINE_POINTS} points.`);
      }
      const points = primitive.points.map((candidate, index) =>
        point(candidate, `polyline.points[${index}]`),
      );
      if (!points.some((candidate) => hasLength(points[0]!, candidate))) {
        fail("Guide polyline cannot be empty.");
      }
      if (primitive.closed !== undefined && typeof primitive.closed !== "boolean") {
        fail("Guide polyline closed must be boolean.");
      }
      return {
        type: "polyline",
        points,
        ...(primitive.closed === true ? { closed: true } : {}),
      };
    }
    case "rectangle": {
      const x = normalized(primitive.x, "rectangle.x");
      const y = normalized(primitive.y, "rectangle.y");
      const width = normalized(primitive.width, "rectangle.width");
      const height = normalized(primitive.height, "rectangle.height");
      if (width <= 0 || height <= 0 || x + width > 1 || y + height > 1) {
        fail("Guide rectangle must have positive dimensions inside normalized bounds.");
      }
      const cornerRadius =
        primitive.cornerRadius === undefined
          ? undefined
          : normalized(primitive.cornerRadius, "rectangle.cornerRadius");
      if (cornerRadius !== undefined && cornerRadius > Math.min(width, height) / 2) {
        fail("Guide rectangle corner radius is too large.");
      }
      return {
        type: "rectangle",
        x,
        y,
        width,
        height,
        ...(cornerRadius !== undefined ? { cornerRadius } : {}),
      };
    }
    case "ellipse": {
      const cx = normalized(primitive.cx, "ellipse.cx");
      const cy = normalized(primitive.cy, "ellipse.cy");
      const rx = normalized(primitive.rx, "ellipse.rx");
      const ry = normalized(primitive.ry, "ellipse.ry");
      if (rx <= 0 || ry <= 0 || cx - rx < 0 || cx + rx > 1 || cy - ry < 0 || cy + ry > 1) {
        fail("Guide ellipse must have positive radii inside normalized bounds.");
      }
      return { type: "ellipse", cx, cy, rx, ry };
    }
    case "arc": {
      const cx = normalized(primitive.cx, "arc.cx");
      const cy = normalized(primitive.cy, "arc.cy");
      const radius = normalized(primitive.radius, "arc.radius");
      const startAngle = finite(primitive.startAngle, "arc.startAngle");
      const endAngle = finite(primitive.endAngle, "arc.endAngle");
      if (
        radius <= 0 ||
        cx - radius < 0 ||
        cx + radius > 1 ||
        cy - radius < 0 ||
        cy + radius > 1 ||
        Math.abs(startAngle) > MAX_ABSOLUTE_ARC_ANGLE ||
        Math.abs(endAngle) > MAX_ABSOLUTE_ARC_ANGLE ||
        Math.abs(endAngle - startAngle) <= Number.EPSILON ||
        Math.abs(endAngle - startAngle) > MAX_ABSOLUTE_ARC_ANGLE
      ) {
        fail("Guide arc geometry is invalid.");
      }
      if (primitive.anticlockwise !== undefined && typeof primitive.anticlockwise !== "boolean") {
        fail("Guide arc direction must be boolean.");
      }
      return {
        type: "arc",
        cx,
        cy,
        radius,
        startAngle,
        endAngle,
        ...(primitive.anticlockwise === true ? { anticlockwise: true } : {}),
      };
    }
    case "bezier": {
      const start = point(primitive.start, "bezier.start");
      const control1 = point(primitive.control1, "bezier.control1");
      const control2 = point(primitive.control2, "bezier.control2");
      const end = point(primitive.end, "bezier.end");
      if (!hasLength(start, end)) fail("Guide Bezier curve cannot be empty.");
      return { type: "bezier", start, control1, control2, end };
    }
    default:
      return fail("Unknown guide primitive type.");
  }
}

function safeText(value: unknown, label: string): string {
  if (
    typeof value !== "string" ||
    value.trim().length === 0 ||
    value.length > MAX_ACTIVITY_TEXT_LENGTH
  ) {
    fail(`${label} is invalid.`);
  }
  return value;
}

function difficulty(value: unknown): ActivityDifficulty {
  if (value !== "Easy" && value !== "Medium") fail("Activity difficulty is invalid.");
  return value;
}

function validatePreview(value: unknown): readonly GuidePrimitive[] {
  if (!Array.isArray(value) || value.length === 0 || value.length > MAX_PREVIEW_PRIMITIVES) {
    fail(`Activity preview must contain 1 to ${MAX_PREVIEW_PRIMITIVES} primitives.`);
  }
  return value.map(validateGuidePrimitive);
}

export function validateDrawingActivity(value: unknown): DrawingActivity {
  if (!value || typeof value !== "object") fail("Activity must be an object.");
  const activity = value as Record<string, unknown>;
  const id = safeText(activity.id, "Activity ID");
  if (!ACTIVITY_ID.test(id)) fail("Activity ID must be stable kebab-case.");
  const order = finite(activity.order, "Activity order");
  if (!Number.isInteger(order) || order < 1 || order > MAX_ACTIVITIES) {
    fail("Activity order is invalid.");
  }
  const base = {
    id,
    order,
    title: safeText(activity.title, "Activity title"),
    description: safeText(activity.description, "Activity description"),
    difficulty: difficulty(activity.difficulty),
    previewPrimitives: validatePreview(activity.previewPrimitives),
  };

  if (activity.type === "guided") {
    if (
      !Array.isArray(activity.steps) ||
      activity.steps.length < MIN_GUIDE_STEPS ||
      activity.steps.length > MAX_GUIDE_STEPS
    ) {
      fail(`Guided activities require ${MIN_GUIDE_STEPS} to ${MAX_GUIDE_STEPS} steps.`);
    }
    let primitiveCount = 0;
    const stepIds = new Set<string>();
    const steps = activity.steps.map((candidate, index) => {
      if (!candidate || typeof candidate !== "object") fail(`Guide step ${index + 1} is invalid.`);
      const step = candidate as Record<string, unknown>;
      const stepId = safeText(step.id, `Guide step ${index + 1} ID`);
      if (stepIds.has(stepId)) fail("Guide step IDs must be unique.");
      stepIds.add(stepId);
      if (
        !Array.isArray(step.primitives) ||
        step.primitives.length === 0 ||
        step.primitives.length > MAX_PRIMITIVES_PER_STEP
      ) {
        fail(`Guide step primitives exceed the ${MAX_PRIMITIVES_PER_STEP} primitive limit.`);
      }
      primitiveCount += step.primitives.length;
      return {
        id: stepId,
        instruction: safeText(step.instruction, `Guide step ${index + 1} instruction`),
        primitives: step.primitives.map(validateGuidePrimitive),
      };
    });
    if (primitiveCount > MAX_TOTAL_PRIMITIVES_PER_GUIDE) {
      fail(`Guide exceeds the ${MAX_TOTAL_PRIMITIVES_PER_GUIDE} total primitive limit.`);
    }
    return { ...base, type: "guided", steps } satisfies GuidedDrawingActivity;
  }

  if (activity.type === "challenge") {
    return {
      ...base,
      type: "challenge",
      prompt: safeText(activity.prompt, "Challenge prompt"),
      encouragement: safeText(activity.encouragement, "Challenge encouragement"),
    };
  }
  return fail("Activity type is invalid.");
}

export function validateActivityCatalog(value: unknown): readonly DrawingActivity[] {
  if (!Array.isArray(value) || value.length > MAX_ACTIVITIES) {
    fail(`Activity catalogue exceeds the ${MAX_ACTIVITIES} activity limit.`);
  }
  const activities = value.map(validateDrawingActivity);
  const ids = new Set<string>();
  const orders = new Set<number>();
  for (const activity of activities) {
    if (ids.has(activity.id)) fail(`Duplicate activity ID: ${activity.id}`);
    if (orders.has(activity.order)) fail(`Duplicate activity order: ${activity.order}`);
    ids.add(activity.id);
    orders.add(activity.order);
  }
  const guidedCount = activities.filter((activity) => activity.type === "guided").length;
  const challengeCount = activities.length - guidedCount;
  if (guidedCount !== GUIDED_ACTIVITY_COUNT || challengeCount !== CHALLENGE_ACTIVITY_COUNT) {
    fail(
      `Catalogue requires exactly ${GUIDED_ACTIVITY_COUNT} guides and ${CHALLENGE_ACTIVITY_COUNT} challenges.`,
    );
  }
  return [...activities].sort((left, right) => left.order - right.order);
}
